import axiosInstance from '../../../../config/axios';

export interface GoogleBusinessConnectionStatus {
  configured: boolean;
  connected: boolean;
  account_id?: string | null;
  connected_at?: string | null;
  expires_at?: string | null;
  locations_count?: number;
}

export interface GoogleBusinessAccount {
  name: string;
  accountName: string;
  type?: string;
  verificationState?: string;
  vettedState?: string;
}

export interface GoogleBusinessLocation {
  name: string;
  title: string;
  storefrontAddress?: {
    regionCode?: string;
    languageCode?: string;
    postalCode?: string;
    administrativeArea?: string;
    locality?: string;
    addressLines?: string[];
  };
  metadata?: {
    hasGoogleUpdated?: boolean;
    hasPendingEdits?: boolean;
    canOperateLocalPost?: boolean;
    canOperateLodgingData?: boolean;
  };
}

export interface GoogleBusinessConnectedLocation {
  id: number;
  google_location_id: string;
  location_name: string;
  address?: string;
  is_active: boolean;
  connected_at: string;
}

export interface ConnectionStartResponse {
  authorize_url: string;
  state?: string;
}

export interface GoogleBusinessReviewer {
  displayName: string;
  profilePhotoUrl?: string;
  isAnonymous?: boolean;
}

export interface GoogleBusinessReviewReply {
  comment: string;
  updateTime: string;
}

export interface GoogleBusinessReview {
  name: string;
  reviewId: string;
  reviewer: GoogleBusinessReviewer;
  starRating: 'ONE' | 'TWO' | 'THREE' | 'FOUR' | 'FIVE';
  comment?: string;
  createTime: string;
  updateTime: string;
  reviewReply?: GoogleBusinessReviewReply;
}

export interface GoogleBusinessReviewsResponse {
  reviews: GoogleBusinessReview[];
  averageRating?: number;
  totalReviewCount?: number;
  nextPageToken?: string;
}

class GoogleBusinessService {
  private static readonly BASE = '/api/integrations/google-business';

  /**
   * Get current Google Business Profile connection status
   */
  static async getConnectionStatus(): Promise<GoogleBusinessConnectionStatus> {
    const { data } = await axiosInstance.get(`${GoogleBusinessService.BASE}/connection/status/`);
    return data;
  }

  /**
   * Start Google Business Profile OAuth connection flow
   * Returns authorize_url to open in popup
   */
  static async startConnection(): Promise<ConnectionStartResponse> {
    const { data } = await axiosInstance.post(`${GoogleBusinessService.BASE}/connection/start/`);
    return data;
  }

  /**
   * Disconnect Google Business Profile integration
   */
  static async disconnect(): Promise<void> {
    await axiosInstance.post(`${GoogleBusinessService.BASE}/connection/disconnect/`);
  }

  /**
   * Get list of Google Business accounts the user has access to
   */
  static async getAccounts(): Promise<GoogleBusinessAccount[]> {
    const { data } = await axiosInstance.get(`${GoogleBusinessService.BASE}/accounts/`);
    return data?.accounts || [];
  }

  /**
   * Select a Google Business account for the tenant
   */
  static async selectAccount(accountId: string): Promise<void> {
    await axiosInstance.post(`${GoogleBusinessService.BASE}/accounts/select/`, {
      account_id: accountId,
    });
  }

  /**
   * Get list of locations for the selected Google Business account
   */
  static async getLocations(): Promise<{
    locations: GoogleBusinessLocation[];
    connected_location_ids: string[];
  }> {
    const { data } = await axiosInstance.get(`${GoogleBusinessService.BASE}/locations/`);
    return {
      locations: data?.locations || [],
      connected_location_ids: data?.connected_location_ids || [],
    };
  }

  /**
   * Connect a Google Business location for management
   */
  static async connectLocation(params: {
    google_location_id: string;
    location_name: string;
    address?: string;
  }): Promise<GoogleBusinessConnectedLocation> {
    const { data } = await axiosInstance.post(
      `${GoogleBusinessService.BASE}/locations/connect/`,
      params
    );
    return data;
  }

  /**
   * Disconnect a Google Business location
   */
  static async disconnectLocation(locationId: number): Promise<void> {
    await axiosInstance.delete(`${GoogleBusinessService.BASE}/locations/${locationId}/`);
  }

  /**
   * Get list of connected Google Business locations for reviews page
   */
  static async getConnectedLocations(): Promise<GoogleBusinessConnectedLocation[]> {
    const { data } = await axiosInstance.get(`${GoogleBusinessService.BASE}/connected-locations/`);
    return data?.locations || [];
  }

  /**
   * Get reviews for a connected location
   */
  static async getReviews(
    locationId: number,
    pageToken?: string
  ): Promise<GoogleBusinessReviewsResponse> {
    const params = pageToken ? { page_token: pageToken } : {};
    const { data } = await axiosInstance.get(
      `${GoogleBusinessService.BASE}/locations/${locationId}/reviews/`,
      { params }
    );
    return {
      reviews: data?.reviews || [],
      averageRating: data?.averageRating,
      totalReviewCount: data?.totalReviewCount,
      nextPageToken: data?.nextPageToken,
    };
  }

  /**
   * Reply to a review (create or update)
   */
  static async replyToReview(
    locationId: number,
    reviewId: string,
    comment: string
  ): Promise<GoogleBusinessReviewReply> {
    const { data } = await axiosInstance.put(
      `${GoogleBusinessService.BASE}/locations/${locationId}/reviews/${encodeURIComponent(reviewId)}/reply/`,
      { comment }
    );
    return data;
  }

  /**
   * Delete a reply from a review
   */
  static async deleteReply(locationId: number, reviewId: string): Promise<void> {
    await axiosInstance.delete(
      `${GoogleBusinessService.BASE}/locations/${locationId}/reviews/${encodeURIComponent(reviewId)}/reply/`
    );
  }
}

export default GoogleBusinessService;
